import React, { useRef } from "react";
import { IconButton } from "@fluentui/react/lib/Button";
import { IContextualMenuProps } from "@fluentui/react";
import { formatMessageDefault } from "../../../utils/helpers/LanguageHelper";
import { DocumentPermissionTO } from "../../../api/models/document-permission-to";
import { WorkflowStatus } from "../../../api/models/workflow-status";
import { getWorkflowCategory, OutlineFilterStatus } from "../workflowStatusCategory";
import "./outline-search.css";

export interface OutlineSearchResultRowProps {
  row: DocumentPermissionTO;
  isSelected: boolean;
  onSingleClick: (row: DocumentPermissionTO) => void;
  onDoubleClick: (row: DocumentPermissionTO) => void;
  /** Optional factory returning contextual-menu props for the workflow-status */
  getWorkflowStatusMenuProps?: (row: DocumentPermissionTO) => IContextualMenuProps;
}

const SINGLE_CLICK_DELAY_MS = 250;

/** Renders a single search result row with name, workflow status badge and actions */
export const OutlineSearchResultRow: React.FC<OutlineSearchResultRowProps> = ({
  row,
  isSelected,
  onSingleClick,
  onDoubleClick,
  getWorkflowStatusMenuProps,
}) => {
  const singleClickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { documentPart, hasReadPermissions, workflowStatus } = row;

  const handleClick = () => {
    if (singleClickTimer.current) {
      clearTimeout(singleClickTimer.current);
    }
    singleClickTimer.current = setTimeout(() => {
      singleClickTimer.current = null;
      onSingleClick(row);
    }, SINGLE_CLICK_DELAY_MS);
  };

  const handleDoubleClick = () => {
    if (singleClickTimer.current) {
      clearTimeout(singleClickTimer.current);
      singleClickTimer.current = null;
    }
    if (hasReadPermissions) {
      onDoubleClick(row);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onSingleClick(row);
    }
  };

  const statusIconClass = workflowStatus ? getWorkflowIconClass(workflowStatus) : undefined;

  const rowClasses = [
    "outline-search-result-row",
    isSelected ? "outline-search-result-row-selected" : "",
    hasReadPermissions ? "" : "outline-search-result-row-no-perm",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      role="button"
      className={rowClasses}
      tabIndex={0}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onKeyDown={handleKeyDown}
      aria-label={documentPart.name}
      aria-pressed={isSelected}
    >
      {statusIconClass && (
        <IconButton
          className={statusIconClass}
          title={formatMessageDefault("cof.outline.search.workflow", "Workflow")}
          ariaLabel={formatMessageDefault("cof.outline.search.workflow", "Workflow")}
          menuProps={getWorkflowStatusMenuProps?.(row)}
          onRenderMenuIcon={() => <span />}
          onClick={(e) => e.stopPropagation()}
          styles={{ root: { flexShrink: 0, padding: 0 } }}
        />
      )}
      <span className="outline-search-result-name" title={documentPart.name}>
        {documentPart.name}
      </span>
    </div>
  );
};

function getWorkflowIconClass(workflowStatus: WorkflowStatus): string | undefined {
  if (!workflowStatus) return undefined;
  switch (getWorkflowCategory(workflowStatus)) {
    case OutlineFilterStatus.NOT_RELEVANT:
      return "ms-significant_icon";
    case OutlineFilterStatus.IN_PROGRESS:
      return "ms-in_progress_icon";
    case OutlineFilterStatus.TO_BE_APPROVED:
      return "ms-pending_withpermission_icon";
    case OutlineFilterStatus.PENDING_APPROVAL:
      return "ms-pending_withoutpermission_icon";
    case OutlineFilterStatus.REJECTED:
      return "ms-rejection_icon";
    case OutlineFilterStatus.SUBMITTED:
      return "ms-submission_icon";
    default:
      return "ms-initial_state_icon";
  }
}
